import React, { useContext, useEffect } from 'react';
import { Modal, TableWithHeader } from 'dyl-components';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import InvoicesToolbar from './InvoicesToolbar';
import InvoicesTable from './InvoicesTable';
import { useDispatch, useSelector } from 'react-redux';
import usersActions from 'actions/users';

import './index.scss';
import BulkActionsProvider, { BulkActionsContext } from 'shared/context/BulkActionsProvider';
import QuoteBuilderProvider from 'shared/context/QuoteBuilderProvider';
import { KEY_ACCOUNT_NAME, KEY_ACCOUNT_OWNER, KEY_CREATED, KEY_CREATED_BY, KEY_DUE_ON, KEY_INVOICE_ID, KEY_INVOICE_STATUS, KEY_NUMBER_PRODUCTS, KEY_ORDER_ID, KEY_PAYMENT_TYPE, KEY_PRODUCTS, KEY_SUBTOTAL, KEY_BILLED, KEY_TERM } from 'shared/constants/INVOICE_REPORT_COLUMNS';
import invoiceReportActions from 'actions/invoice_report';
import { KEY_ACCOUNT_CO_OWNER } from 'shared/constants/QUOTES_ORDERS_COLUMNS';
import NewActivityForm from 'shared/forms/NewActivity';
import { PhoneUtil } from 'utils';
import useModal from 'utils/useModal';

const InvoicesReport = ({ root }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [params] = useSearchParams();

    const columnDisplayOptions = [KEY_CREATED, KEY_CREATED_BY, KEY_DUE_ON, KEY_INVOICE_ID, KEY_ORDER_ID, KEY_INVOICE_STATUS, KEY_ACCOUNT_OWNER, KEY_ACCOUNT_CO_OWNER, KEY_SUBTOTAL, KEY_PAYMENT_TYPE, KEY_BILLED, KEY_NUMBER_PRODUCTS, KEY_PRODUCTS, KEY_ACCOUNT_NAME, KEY_TERM];
    const defaultColumnsDisplayed = [KEY_CREATED, KEY_CREATED_BY, KEY_DUE_ON, KEY_INVOICE_ID, KEY_ORDER_ID, KEY_INVOICE_STATUS, KEY_ACCOUNT_OWNER, KEY_TERM, KEY_BILLED, KEY_ACCOUNT_NAME];
    const allowedColumns = [
        KEY_CREATED,
        KEY_CREATED_BY,
        KEY_DUE_ON,
        KEY_INVOICE_STATUS,
        KEY_ACCOUNT_NAME,
        KEY_SUBTOTAL,
        KEY_PAYMENT_TYPE,
        KEY_BILLED,
        KEY_PRODUCTS,
        KEY_TERM
    ]
    const columnsParameter = params.get('columns')?.split(',')?.filter(column => columnDisplayOptions.includes(column)) || [];
    const columns = columnsParameter.length ? columnsParameter : defaultColumnsDisplayed;

    const [, setSelectedInvoices, , setAreInvoicesInAllPagesSelected] = useContext(BulkActionsContext);
    const userPhoneCreds = useSelector((state) => state.officeView.creds.extension);
    const isPhoneSystemSupported = PhoneUtil.isPhoneSystemSupported(userPhoneCreds);
    const callActivityModal = useModal();
    const onCallIconClick = (contact_id) => {
        if (!isPhoneSystemSupported) {
            callActivityModal.openModal(contact_id);
        }
    };


    const onPageChange = (_, { activePage }) => {
        const query = new URLSearchParams(params);
        query.set('page', activePage);
        const query_string = query.toString();
        navigate(`${root}/invoices${query_string ? `?${query_string}` : ''}`);
    }

    const isColumnHidden = (column) => {
        return !columns.includes(column);
    }

    const onToggleColumn = async (column) => {
        const updatedColumns = isColumnHidden(column) ? [...columns, column] : columns.filter(visibleColumn => visibleColumn !== column);

        const updateColumnsParameter = (filtersToRemove) => {
            const query = new URLSearchParams(params);
            if (updatedColumns.length) {
                query.set('columns', updatedColumns.join(','));
            } else {
                query.delete('columns');
            }
            if (filtersToRemove) {
                const filters = Array.isArray(filtersToRemove) ? filtersToRemove : [filtersToRemove];
                filters.forEach(f => query.delete(f));
            }
            const query_string = query.toString();
            navigate(`${root}/invoices${query_string ? `?${query_string}` : ''}`);
        }

        if (!isColumnHidden(column)) {
            switch (column) {
                case KEY_CREATED:
                    if (params.get('start') || params.get('end') || params.get('order_by') === 'created') {
                        updateColumnsParameter(['start', 'end', 'order_by', 'order']);
                    } else {
                        updateColumnsParameter();
                    }
                    break;
                case KEY_DUE_ON:
                    if (params.get('order_by') === 'due_on') {
                        updateColumnsParameter(['order_by', 'order']);
                    } else {
                        updateColumnsParameter();
                    }
                    break;
                default:
                    updateColumnsParameter();
            }
        } else {
            updateColumnsParameter();
        }
    }

    const location = useLocation();

    const powerSelectors = useSelector(state => (state.invoice_report.powerSelectors));

    useEffect(() => {
        if (!location.state?.isTogglingPanel && powerSelectors) {
            const queryParameters = {
                page: 1,
                limit: 25,
                ...Object.fromEntries(params),
                columns: [...new Set([...columns.filter(column => allowedColumns.includes(column)), KEY_ACCOUNT_NAME])].join(",")
            }
            dispatch(invoiceReportActions.readAll({
                ...queryParameters,
            }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params, powerSelectors])

    useEffect(() => {
        if (!location.state?.isTogglingPanel) {
            dispatch(usersActions.readUsers());
            setSelectedInvoices([]);
            setAreInvoicesInAllPagesSelected(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params]);

    useEffect(() => {
        dispatch(invoiceReportActions.readPowerSelectors());
    }, [dispatch])

    return (
        <div className="InvoicesPage">
            <Modal
                open={callActivityModal?.isVisible}
                onClose={callActivityModal?.closeModal}
                size="tiny"
            >
                <Modal.Content>
                    <NewActivityForm
                        onClose={callActivityModal?.closeModal}
                        hideActivitySelection
                        contact={{ id: callActivityModal?.data }}
                        isContact
                    />
                </Modal.Content>
            </Modal>
            <TableWithHeader
                header={
                    <InvoicesToolbar
                        isColumnHidden={isColumnHidden}
                        onToggleColumn={onToggleColumn}
                    />
                }
                table={
                    <InvoicesTable
                        isColumnHidden={isColumnHidden}
                        onPageChange={onPageChange}
                        onCallIconClick={onCallIconClick}
                    />
                }
            />
        </div>
    );
}

export default function InvoicesContainer({ root = '/settings' }) {
    return (
        <BulkActionsProvider>
            <QuoteBuilderProvider>
                <InvoicesReport root={root} />
            </QuoteBuilderProvider>
        </BulkActionsProvider>
    )
};
